'use client';

import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { ControlProps, CSSObjectWithLabel } from 'react-select';

// Dynamically import react-select to avoid SSR issues
const Select = dynamic(() => import('react-select'), {
  ssr: false,
  loading: () => (
    <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
      Loading...
    </div>
  )
});

export interface SearchableOption {
  value: string;
  label: string;
  id?: string;
}

interface SearchableDropdownProps {
  options: SearchableOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  className?: string;
  label?: string;
  required?: boolean;
  error?: string;
  name?: string;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  isSearchable = true,
  isClearable = true,
  isDisabled = false,
  isLoading = false,
  className = '',
  label,
  required = false,
  error,
  name
}) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const customStyles = {
    control: (base: CSSObjectWithLabel, props: ControlProps<SearchableOption, false>) => ({
      ...base,
      minHeight: '48px', // Match py-3 (12px top + 12px bottom + 24px content = 48px)
      padding: '0 16px', // Match px-4
      border: error 
        ? '1px solid #ef4444' 
        : props.isFocused 
          ? '2px solid #6B39FF' 
          : '1px solid #d1d5db',
      borderRadius: '8px', // Match rounded-lg
      boxShadow: props.isFocused ? '0 0 0 1px #6B39FF' : 'none',
      fontSize: '16px', // Match default text size
      '&:hover': {
        border: error ? '1px solid #ef4444' : '1px solid #9ca3af'
      }
    }),
    option: (base: CSSObjectWithLabel, props: { isSelected: boolean; isFocused: boolean }) => ({
      ...base,
      padding: '12px 16px', // Match py-3 px-4
      fontSize: '16px', // Match default text size
      backgroundColor: props.isSelected 
        ? '#6B39FF' 
        : props.isFocused 
          ? '#f3f4f6' 
          : 'white',
      color: props.isSelected ? 'white' : '#374151',
      '&:hover': {
        backgroundColor: props.isSelected ? '#5A2FE8' : '#f3f4f6'
      }
    }),
    placeholder: (base: CSSObjectWithLabel) => ({
      ...base,
      color: '#9ca3af',
      fontSize: '16px' // Match default text size
    }),
    singleValue: (base: CSSObjectWithLabel) => ({
      ...base,
      color: '#374151',
      fontSize: '16px' // Match default text size
    }),
    input: (base: CSSObjectWithLabel) => ({
      ...base,
      fontSize: '16px', // Match default text size
      margin: '0',
      padding: '0'
    }),
    valueContainer: (base: CSSObjectWithLabel) => ({
      ...base,
      padding: '0'
    }),
    indicatorsContainer: (base: CSSObjectWithLabel) => ({
      ...base,
      padding: '0 4px 0 0' // Minimal padding, no extra space
    }),
    dropdownIndicator: (base: CSSObjectWithLabel) => ({
      ...base,
      padding: '0 8px', // Consistent with regular dropdown
      color: '#9ca3af' // Match gray-400 color
    })
  };

  // Convert value to selected option
  const selectedOption = value ? options.find(option => option.value === value) : null;

  // Show placeholder during SSR and initial mount
  if (!isMounted) {
    return (
      <div className={className}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-base">
          {placeholder}
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <Select
        options={options}
        value={selectedOption}
        onChange={(selectedOption: unknown) => {
          const option = selectedOption as SearchableOption | null;
          onChange(option ? option.value : '');
        }}
        isSearchable={isSearchable}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isLoading={isLoading}
        placeholder={placeholder}
        styles={customStyles as any} // eslint-disable-line @typescript-eslint/no-explicit-any
        className="react-select-container"
        classNamePrefix="react-select"
        noOptionsMessage={() => 'No options available'}
        loadingMessage={() => 'Loading...'}
        instanceId={`react-select-${name || Math.random().toString(36).substr(2, 9)}`}
        name={name}
      />
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default SearchableDropdown;
